import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

@customElement('upgrade-modal')
export class UpgradeModal extends LitElement {
  @property({ type: Boolean })
  open = false;

  @property({ type: String })
  featureName = '';

  @property({ type: String })
  actionName = '';

  static styles = css`
    .modal-content {
      text-align: center;
      padding: 1rem;
    }

    .feature-icon {
      font-size: 3rem;
      color: var(--sl-color-primary-600);
      margin-bottom: 1rem;
    }

    .feature-title {
      font-size: 1.5rem;
      font-weight: 600;
      margin-bottom: 0.5rem;
      color: var(--sl-color-neutral-900);
    }

    .feature-description {
      color: var(--sl-color-neutral-600);
      margin-bottom: 1.5rem;
      line-height: 1.6;
    }

    .current-plan {
      background: var(--sl-color-neutral-100);
      padding: 0.5rem 1rem;
      border-radius: 20px;
      font-size: 0.9rem;
      color: var(--sl-color-neutral-700);
      margin-bottom: 1.5rem;
      display: inline-block;
    }

    .upgrade-benefits {
      text-align: left;
      margin: 1.5rem 0;
      background: var(--sl-color-primary-50);
      padding: 1rem;
      border-radius: 8px;
    }

    .benefit-item {
      display: flex;
      align-items: center;
      margin-bottom: 0.5rem;
      font-size: 0.9rem;
    }

    .benefit-item sl-icon {
      margin-right: 0.5rem;
      color: var(--sl-color-success-600);
    }

    .modal-actions {
      display: flex;
      gap: 1rem;
      justify-content: center;
      margin-top: 2rem;
    }

    .upgrade-button {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      border: none;
      padding: 0.75rem 1.5rem;
      border-radius: 6px;
      font-weight: 600;
      cursor: pointer;
      transition: transform 0.2s ease;
    }

    .upgrade-button:hover {
      transform: translateY(-1px);
    }

    .cancel-button {
      background: transparent;
      color: var(--sl-color-neutral-600);
      border: 1px solid var(--sl-color-neutral-300);
      padding: 0.75rem 1.5rem;
      border-radius: 6px;
      cursor: pointer;
    }
  `;

  private handleUpgrade() {
    this.dispatchEvent(new CustomEvent('upgrade-confirmed', {
      detail: { feature: this.featureName, action: this.actionName },
      bubbles: true,
      composed: true
    }));
    this.open = false;
  }

  private handleCancel() {
    this.open = false;
  }

  render() {
    return html`
      <sl-dialog 
        ?open=${this.open} 
        @sl-request-close=${this.handleCancel}
        label="Upgrade Required"
      >
        <div class="modal-content">
          <sl-icon name="star-fill" class="feature-icon"></sl-icon>
          
          <div class="current-plan">Current Plan: Basic</div>
          
          <h3 class="feature-title">${this.actionName} - ${this.featureName}</h3>
          <p class="feature-description">
            This feature is available in our Professional and Enterprise plans. 
            Upgrade now to unlock powerful ${this.featureName.toLowerCase()} capabilities!
          </p>

          <div class="upgrade-benefits">
            <div class="benefit-item">
              <sl-icon name="check-circle-fill"></sl-icon>
              Unlimited ${this.featureName.toLowerCase()} management
            </div>
            <div class="benefit-item">
              <sl-icon name="check-circle-fill"></sl-icon>
              Advanced reporting and analytics
            </div>
            <div class="benefit-item">
              <sl-icon name="check-circle-fill"></sl-icon>
              Priority customer support
            </div>
            <div class="benefit-item">
              <sl-icon name="check-circle-fill"></sl-icon>
              Integration capabilities
            </div>
          </div>

          <div class="modal-actions">
            <button class="cancel-button" @click=${this.handleCancel}>
              Maybe Later
            </button>
            <button class="upgrade-button" @click=${this.handleUpgrade}>
              Upgrade Now
            </button>
          </div>
        </div>
      </sl-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'upgrade-modal': UpgradeModal;
  }
}
